/**
 * @module app.cm
 */

import { apiCall } from 'app/shared/lib/api/api';
import { bindable } from 'aurelia-typed-observable-plugin';
import { CaseService } from 'app/cm/services/case-service';
import { ComponentBase, Sorting, IForm } from 'app/shared/lib/essette-site';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { deepClone } from 'app/shared/lib/utilities';
import { EssModalCustomElement } from 'app/shared/elements/ess-dialog';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { EssTableCustomElement, ExpandEvent } from 'app/shared/elements/ess-table';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { SaveReferralTypeCustomElement } from 'app/cm/admin/referral/save-referral-type';
import { TaskQueue, autoinject } from 'aurelia-framework';
import { Toaster } from 'app/shared/lib/toaster';
import { validate } from 'app/shared/elements/ess-rule';
import * as CM from 'app/cm/models/essette-api';
import { EssSortableListCustomElement, IListItem } from 'app/shared/elements/ess-sortable-list';

const expandedReferralTypesCache = new Map<string, Array<CM.ReferralType>>();
export interface ReferralOrderItem extends IListItem {
    id: string;
}

@autoinject
export class ReferralTypes extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected expandedCacheKey = "referralTypes";

    @bindable
    protected rememberExpand: boolean = true;

    protected referralTypes: CM.ReferralType[] = [];
    protected referralTypesOrder: ReferralOrderItem[] = [];
    protected sorting = new Sorting();
    protected pageSize = 25;
    protected resultCount = 0;
    protected includeInactive = false;
    protected validateForm = validate;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $table!: EssTableCustomElement<CM.ReferralType[]>;
    protected $addReferralTypeModal!: EssModalCustomElement;
    protected $sortReferralTypeModal!: EssModalCustomElement;
    protected $form!: IForm;
    protected $saveReferralType!: SaveReferralTypeCustomElement;
    protected $referralTypeList!: EssSortableListCustomElement<ReferralOrderItem>;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CaseService,
        private readonly queue: TaskQueue,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected async attached(): Promise<void> {
        await this.reload();
    }

    ////****************** Events ***************************************************////

    protected formSuccess({ detail }: FormSuccessCustomEvent) {
        this.$addReferralTypeModal.close();

        if(detail.form.reset) detail.form.reset();

        // reload grid and reset page
        this.reload();
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadReferralTypes();
        await this.loadReferralTypes(true); // load all referral types, including inactive, for sorting purposes
    }

    protected restoreExpanded() {
        if(!this.rememberExpand) { return; }
        // we need to give aurelia a chance to bind the outcomes into the
        // table before we ask the table to expand the rows
        this.queue.queueTask(() => {
            if(!this.expandedCacheKey) { return; }
            const expandedReferralTypes = expandedReferralTypesCache.get(this.expandedCacheKey);
            if(expandedReferralTypes !== undefined) {
                this.$table.restoreExpanded(expandedReferralTypes);
            }
        });
    }

    protected trackExpanded({ detail }: ExpandEvent<CM.ReferralType>) {
        if(!this.rememberExpand || !this.expandedCacheKey) { return; }
        expandedReferralTypesCache.set(this.expandedCacheKey, detail.items);
    }

    protected openAddReferralTypeModal() {
        this.$addReferralTypeModal.open();
    }

    protected closeAddReferralTypeModal(): void {
        if(this.$form.reset) this.$form.reset();
        this.$addReferralTypeModal.close();
    }

    private openEditReferralTypeModal(referralType: CM.ReferralType): void {
        if(!referralType || !this.$addReferralTypeModal) {
            return;
        }

        this.$saveReferralType.referralType = deepClone(referralType);
        this.$addReferralTypeModal.open();
    }

    protected openSortReferralTypeModal() {
        this.$sortReferralTypeModal.open();
    }

    protected closeSortReferralTypeModal(): void {
        if(this.$referralTypeList.reset) this.$referralTypeList.reset();
        this.$sortReferralTypeModal.close();
    }

    protected async saveReferralTypeOrder(): Promise<void> {
        await this.sortReferralTypes();
        this.closeSortReferralTypeModal();
        await this.reload();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadReferralTypes(forSort: boolean = false): Promise<void> {
        const includes: string[] = ['criteria'];
        const fields: string[] = ['id', '$display', 'code', ' requirements.*', 'criteria.id'];
        const options: Partial<IQueryLoadOptions> = forSort ? { includeInactive: true } :
            // options for standard load (includes paging)
            {
                top: this.pageSize,
                sorting: this.sorting,
                page: this.$pager?.page || 0,
                includeInactive: this.includeInactive
            };

        try {
            this.state.beginLoad();

            const response = await this.service.loadReferralTypes(options, includes, fields);
            if(forSort) {
                // construct list to keep track of referral type order
                this.referralTypesOrder = response.results.map((item, index) => ({
                    id: item.id,
                    value: item.$display,
                    order: index,
                    isActive: item.isActive
                } as ReferralOrderItem));
            } else {
                this.referralTypes = response.results;
                this.resultCount = response.count || 0;
                this.restoreExpanded();
            }
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateReferralType(referralType: CM.ReferralType, isActive: boolean) {
        if(!referralType?.id) return;
        if(!isActive) {
            const result = await confirmation.showDeactivate('cm:referral.type.$');
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }
            if(!(result.dialogResponse === confirmation.options.OK)) {
                return;
            }
        }

        try {
            this.state.beginSave();
            const response = await this.service.activateReferralType(referralType.id!, isActive);

            if(response.isSuccessful) {
                this.reload();
            }
            this.toaster.standard.activate('cm:referral.type.$', isActive, response.isSuccessful);

        } catch(error) {
            this.toaster.standard.activate('cm:referral.type.$', isActive, false);
            throw error;
        } finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async sortReferralTypes(): Promise<void> {
        let isSuccessful = false;
        try {
            this.state.beginLoad();

            const response = await this.service.sortReferralTypes(this.referralTypesOrder.map(item => item.id));
            isSuccessful = response.isSuccessful;
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.sorted('cm:referral.type.$_plural', isSuccessful);
            this.state.endLoad();
        }
    }
}
